import React, { useState } from 'react';
import { useShop } from '../../context/ShopContext';
import { formatINR } from '../../data/products';
import { 
  BarChart3, 
  TrendingUp, 
  PieChart, 
  Download, 
  Calendar, 
  Printer, 
  FileSpreadsheet, 
  DollarSign, 
  Gem, 
  Package, 
  Percent, 
  ArrowUpRight 
} from 'lucide-react';

export default function AdminReports() {
  const { orders, products, masterMetalRates, addToast } = useShop();

  const [dateRange, setDateRange] = useState('monthly'); // 'daily', 'weekly', 'monthly', 'yearly'

  const totalRevenue = orders.reduce((sum, ord) => sum + (ord.total || 0), 0);
  const totalMakingRevenue = Math.round(totalRevenue * 0.11);
  const totalPureMetalTurnover = Math.round(totalRevenue * 0.86);
  const totalGSTTurnover = Math.round(totalRevenue * 0.03);

  const handleExportCSV = () => {
    const headers = ['Report Metric,Amount (INR),Notes'];
    const rows = [
      `"Total Gross Jewellery Turnover","${totalRevenue}","Includes GST and Making"`,
      `"Pure Metal Asset Turnover","${totalPureMetalTurnover}","Bullion Asset Base"`,
      `"Making Charges Margin Profit","${totalMakingRevenue}","Gross Artisan Margin"`,
      `"Statutory GST Collected (3%)","${totalGSTTurnover}","Govt Tax Reserve"`,
      `"Completed Vault Orders","${orders.length}","Dispatched & Delivered"`
    ];
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers, ...rows].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `ML_Jewellers_Financial_Report_${dateRange}_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    addToast?.('Financial Analytics exported to CSV', 'success');
  };

  return (
    <div style={{ maxWidth: '1240px', margin: '0 auto' }}>
      
      {/* Top Banner */}
      <div style={{
        background: '#ffffff',
        borderRadius: '8px',
        padding: '1.25rem 1.6rem',
        border: '1px solid #e0e0e0',
        marginBottom: '1.5rem',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
      }}>
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: '#2874f0', fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '0.2rem' }}>
            <BarChart3 size={14} />
            <span>EXECUTIVE AUDIT & FINANCIAL PERFORMANCE</span>
          </div>
          <h2 style={{ fontSize: '1.45rem', fontWeight: 800, color: '#212121', margin: 0 }}>
            Reports, Profit Margins & Sales Analytics
          </h2>
          <p style={{ margin: '0.2rem 0 0', fontSize: '0.82rem', color: '#616161' }}>
            Revenue split by pure bullion vs making charges, category performance, GST liabilities and export sheets.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', background: '#f1f3f6', padding: '0.2rem', borderRadius: '4px' }}>
            {['daily', 'weekly', 'monthly', 'yearly'].map(t => (
              <button
                key={t}
                onClick={() => setDateRange(t)}
                style={{
                  padding: '0.35rem 0.75rem',
                  borderRadius: '3px',
                  border: 'none',
                  background: dateRange === t ? '#2874f0' : 'transparent',
                  color: dateRange === t ? '#fff' : '#424242',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  textTransform: 'capitalize'
                }}
              >
                {t}
              </button>
            ))}
          </div>

          <button
            onClick={handleExportCSV}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem',
              padding: '0.55rem 1rem',
              borderRadius: '4px',
              border: 'none',
              background: '#15803d',
              color: '#ffffff',
              fontWeight: 700,
              fontSize: '0.82rem',
              cursor: 'pointer'
            }}
          >
            <Download size={15} />
            <span>Download CSV</span>
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'TOTAL GROSS TURNOVER', val: formatINR(totalRevenue), sub: '+19.2% YoY growth', color: '#15803d' },
          { label: 'PURE BULLION TURNOVER', val: formatINR(totalPureMetalTurnover), sub: 'Cost of precious metal', color: '#d97706' },
          { label: 'MAKING CHARGES PROFIT', val: formatINR(totalMakingRevenue), sub: 'Gross Artisan Margin (11%)', color: '#2563eb' },
          { label: 'STATUTORY GST RESERVE', val: formatINR(totalGSTTurnover), sub: '3% Bullion / Jewellery tax', color: '#7c3aed' }
        ].map(k => (
          <div key={k.label} style={{ background: '#ffffff', padding: '1.1rem 1.3rem', borderRadius: '8px', border: '1px solid #e0e0e0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 800, color: '#757575', letterSpacing: '0.5px' }}>{k.label}</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: k.color, margin: '0.3rem 0' }}>{k.val}</div>
            <div style={{ fontSize: '0.72rem', color: '#9e9e9e' }}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Visual Analytics Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
        
        {/* Category-wise Sales Distribution */}
        <div style={{ background: '#ffffff', borderRadius: '8px', padding: '1.4rem', border: '1px solid #e0e0e0' }}>
          <h3 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#1e293b', margin: '0 0 1.2rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <PieChart size={17} color="#2874f0" />
            <span>Category-Wise Sales Contribution</span>
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {[
              { label: 'Bridal Polki & Jadau Jewellery', share: '46%', amount: totalRevenue * 0.46, color: '#f59e0b' },
              { label: 'Daily Wear Gold Rings & Chains', share: '28%', amount: totalRevenue * 0.28, color: '#3b82f6' },
              { label: '999 Pure Silverware & Utensils', share: '16%', amount: totalRevenue * 0.16, color: '#64748b' },
              { label: 'Luxury Kitchenware & Cookware', share: '10%', amount: totalRevenue * 0.10, color: '#10b981' }
            ].map(c => (
              <div key={c.label}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.84rem', fontWeight: 700, color: '#334155', marginBottom: '0.3rem' }}>
                  <span>{c.label} ({c.share})</span>
                  <span>{formatINR(Math.round(c.amount))}</span>
                </div>
                <div style={{ height: '8px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: c.share, background: c.color, borderRadius: '4px' }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Best-Selling Jewellery Pieces */}
        <div style={{ background: '#ffffff', borderRadius: '8px', padding: '1.4rem', border: '1px solid #e0e0e0' }}>
          <h3 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#1e293b', margin: '0 0 1.2rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <Gem size={17} color="#d97706" />
            <span>Top Performing Masterpieces</span>
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
            {products.slice(0, 5).map((p, idx) => (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.6rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                  <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#e0e7ff', color: '#3730a3', fontSize: '0.72rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {idx + 1}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.82rem' }}>{p.name}</div>
                    <div style={{ fontSize: '0.72rem', color: '#64748b' }}>{p.metalType || 'Gold'} • {p.purity || '22KT'}</div>
                  </div>
                </div>
                <div style={{ textAlign: 'right', fontWeight: 800, color: '#0f172a', fontSize: '0.85rem' }}>
                  {formatINR(p.price || 48000)}
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>

    </div>
  );
}
